import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  Building2,
  IdCard,
  Inbox,
  LayoutDashboard,
  Server,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/auth-context'
import { authMode } from '@/lib/auth/session'
import { ChangePasswordModal } from '@/components/ChangePasswordModal'
import { UserMenu } from '@/components/UserMenu'
import { ATFLogo } from '@/components/assets/ATFLogo'
import {
  CHARCOAL,
  DARK,
  GOLD,
  GOLD_DIM,
  GREY_200,
  GREY_300,
  GREY_400,
  GREY_500,
  WARNING,
  WHITE,
} from '@/lib/genesis'

// Three-surface UI alignment pass (three-surface-ui-alignment): rebuilds
// this shell onto the ui/ app's sidebar idiom (ui/src/components/layout/
// RootLayout.tsx + Sidebar.tsx) -- 240px sidebar, logo lockup top, gold
// active nav state, glass-card content chrome -- rather than the bare
// neutral-800 border layout this file previously had. Every route below
// renders through this shell unmodified; only the chrome changed.
//
// ui-container-nav-usermenu pass: (1) content container now mirrors ui/'s
// per-route wrapper (max-w-[1200px] mx-auto, 40px/lg horizontal padding)
// instead of a flat p-6 with no max-width; (2) every nav item gets a
// lucide-react icon at w-5 h-5, matching ui/'s Sidebar.tsx icon sizing;
// (3) the footer identity block is replaced by the shared UserMenu
// component -- a click-to-open popover chip (avatar + name/email) that
// moves role off the always-visible chip and into the popover, matching
// the operator's ask that role not be the default-visible label. WhoAmI's
// WhoAmIOperator message (console.proto) carries only id/email/role -- no
// name field -- so the chip always falls back to email here, same as the
// customer portal's WhoAmICustomer. Threading a name through would need a
// proto change, out of scope for this pass.
const NAV_ITEMS: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/', label: 'Fleet', icon: Server },
  { to: '/accounts', label: 'Accounts', icon: Building2 },
  { to: '/licenses', label: 'Licenses', icon: IdCard },
  { to: '/usage', label: 'Usage', icon: BarChart3 },
  { to: '/operators', label: 'Operators', icon: Users },
  { to: '/customer-requests', label: 'Customer Requests', icon: Inbox },
]

export function RootLayout({ children }: { children: ReactNode }) {
  const { status, role, principal, logout } = useAuth()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [changingPassword, setChangingPassword] = useState(false)
  // Change-password is only meaningful for a cookie (password-login) session —
  // the endpoint is session+CSRF gated, so a key-mode operator has no password
  // to change. Only surface the trigger in cookie mode.
  const canChangePassword = authMode() === 'cookie'

  if (pathname === '/login') {
    return <>{children}</>
  }

  if (status === 'checking') {
    return (
      <div
        className="flex h-screen items-center justify-center text-sm"
        style={{ backgroundColor: CHARCOAL, color: GREY_400 }}
      >
        Checking session…
      </div>
    )
  }

  if (status === 'unauthenticated') {
    navigate({ to: '/login' })
    return null
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: CHARCOAL }}>
      {/* Sidebar -- 240px, mirrors ui/'s Sidebar (logo lockup top, gold
          active state, muted-grey inactive, footer identity + actions). */}
      <aside
        className="fixed top-0 bottom-0 left-0 z-40 flex w-60 flex-col"
        style={{
          background: `linear-gradient(to bottom, ${DARK}, ${CHARCOAL})`,
          borderRight: `1px solid ${GREY_500}`,
        }}
      >
        <div
          className="flex h-[60px] items-center gap-2 px-4"
          style={{ borderBottom: `1px solid ${GREY_500}` }}
        >
          <ATFLogo variant="icon" size="sm" style={{ color: GOLD }} />
          <div className="flex flex-col leading-none">
            <span className="font-display text-sm font-semibold" style={{ color: WHITE }}>
              ATF Console
            </span>
            <span
              className="mt-0.5 inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide"
              style={{ backgroundColor: GOLD_DIM, color: GOLD }}
            >
              Operator Control Plane
            </span>
          </div>
        </div>

        <nav aria-label="Main navigation" className="flex-1 overflow-y-auto p-3">
          <ul role="list" className="m-0 flex list-none flex-col gap-1 p-0">
            {NAV_ITEMS.map((item) => {
              const active =
                pathname === item.to || (item.to !== '/' && pathname.startsWith(item.to + '/'))
              const Icon = item.icon
              return (
                <li key={item.to} role="listitem">
                  <Link
                    to={item.to}
                    className="glass-button flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors"
                    activeOptions={{ exact: item.to === '/' }}
                    style={{
                      color: active ? GOLD : GREY_300,
                      background: active ? GOLD_DIM : undefined,
                      border: active ? '1px solid transparent' : undefined,
                      fontWeight: active ? 500 : 400,
                    }}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* Footer -- congruent user-menu pattern across all three UI
            services (operator feedback): avatar + name/email chip, role
            moved into the click-to-open popover instead of shown by
            default. The viewer-role mutation-hidden notice is real
            functional information (not chrome) and stays visible above
            the menu, matching where it appeared before this pass. */}
        <div className="p-3" style={{ borderTop: `1px solid ${GREY_500}` }}>
          {role === 'viewer' && (
            <div
              className="mb-3 rounded-md px-2 py-1 text-xs"
              style={{ backgroundColor: DARK, borderLeft: `3px solid ${WARNING}`, color: GREY_200 }}
            >
              Viewer role: mutation controls are hidden.
            </div>
          )}
          {principal && (
            <UserMenu
              email={principal.email}
              roleLabel={role === 'admin' ? 'Admin' : role === 'viewer' ? 'Viewer' : 'unknown'}
              onChangePassword={canChangePassword ? () => setChangingPassword(true) : undefined}
              onSignOut={() => {
                // logout is async (cookie mode revokes the server session);
                // fire-and-forget is fine — it flips status to unauthenticated
                // on resolve, and any /auth/logout network failure still clears
                // local state (auth-context handles it), so the operator is
                // never trapped in the app.
                void logout()
              }}
            />
          )}
        </div>
      </aside>

      <main className="atf-grid-bg ml-60 flex-1 overflow-y-auto" style={{ backgroundColor: CHARCOAL }}>
        <div className="min-h-screen px-4 pt-6 pb-6 lg:px-10">
          <div className="mx-auto max-w-[1200px]">{children}</div>
        </div>
      </main>
      {changingPassword && (
        <ChangePasswordModal onClose={() => setChangingPassword(false)} />
      )}
    </div>
  )
}
